/* eslint-disable max-lines */
/* istanbul ignore file */
import moment from 'moment';
import AppRouter from 'common/AppRouter';
import {
  formatUTCToTimezone,
  getTimezone,
  pluralize
} from 'common/utils/utils';
import {
  SEARCH_DATE_FORMAT,
  TIME_PICKER_FORMAT
} from 'common/constants/constants';
import { LINE_HEIGHT } from 'common/constants/sizes';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import {
  notificationPredictionType,
  formatReviewTimeDaysAndHours,
  addVerboseReviewTimeIfNeeded,
  TWO_WEEKS_IN_DAYS
} from './notifications.service';
import { getFormattedDailyTime } from '../General/general-settings.service';
import { SettingValues } from '@linearb/linjs-common';
import { METRICS_METADATA } from 'views/root/views/home/views/performance/views/CustomReport/consts';
import { LBButton } from '@linearb/buzz';

const flexStyle = { direction: 'row', items: 'center' };
const textStyle = { size: 'medium' };

export const NOTIFICATION_ENABLED_SETTING_KEYS = {
  WARNING_ENABLED: 'warning_enabled',
  VIOLATION_ENABLED: 'violation_enabled',
  ENABLED: 'enabled'
};

export const NOTIFICATION_THRESHOLDS_TO_OMIT_KEYS = {
  BY_GOALS_MIGRATION: 'by_goals_migration'
};

export const NOTIFICATION_TYPES = {
  HEADSUP: {
    type: 'headsup',
    tooltipTitle: 'Send Team Heads-Up'
  },
  NOTIFICATIONS: {
    type: 'notifications',
    tooltipTitle: 'Send Team Notification'
  }
};

const violationEnabledKey = {
  violationEnabledKey: NOTIFICATION_ENABLED_SETTING_KEYS.VIOLATION_ENABLED
};

const warningEnabledKey = {
  warningEnabledKey: NOTIFICATION_ENABLED_SETTING_KEYS.WARNING_ENABLED
};

const enabledKey = {
  violationEnabledKey: NOTIFICATION_ENABLED_SETTING_KEYS.ENABLED
};

const defaultEnabledValues = {
  ...violationEnabledKey,
  ...warningEnabledKey
};

export const NOTIFICATION_SETTING_NAMES = {
  warningEnabledKey: 'Heads up',
  violationEnabledKey: 'Team'
};

export const pullRequestsLargerThan = () => ({
  [SettingValues.PULL_REQUESTS_LARGER_THAN]: {
    widgetId: 27,
    name: METRICS_METADATA[27].settingTitle,
    description: ({ pr_size }) => (
      <>
        Avoid merging pull requests with more than <b>{pr_size} code changes</b>
      </>
    ),
    thresholds: ({ pr_size }) => [
      {
        pr_size: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                A pull request has more than <b>{pr_size}</b> code changes
              </Text>
            </Flex>
          ),
          sliderSteps: 10
        }
      }
    ],
    isHeadsUp: true,
    prediction: ({ pr_size, startDate, endDate }) => [
      {
        type: notificationPredictionType.PRS,
        filters: {
          is_draft: false,
          pr_size: { gt: pr_size },
          updated_at: { before: endDate, after: startDate }
        }
      }
    ],
    tooltip: () => 'Notify: when a large PR has been merged',
    headsUpTooltip: () => 'Heads-up: when a large PR has been opened',
    ...defaultEnabledValues
  }
});

export const pullRequestsOpenMoreThan = () => ({
  [SettingValues.PULL_REQUESTS_OPEN_MORE_THAN]: {
    widgetId: 28,
    name: METRICS_METADATA[28].settingTitle,
    description: ({ review_time }) => (
      <>
        Merge pull requests in less than{' '}
        <b>{formatReviewTimeDaysAndHours(review_time)}</b>
      </>
    ),
    thresholds: ({ review_time }) => [
      {
        review_time: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                A pull request was merged after more than <b>{review_time}</b>{' '}
                {pluralize(review_time, 'hour', 's', false)}{' '}
                {addVerboseReviewTimeIfNeeded(review_time)}
              </Text>
            </Flex>
          ),
          minMaxFormatter: val => val
        }
      }
    ],
    isHeadsUp: true,
    prediction: ({ review_time }) => [
      {
        type: notificationPredictionType.PRS,
        filters: {
          is_draft: false,
          state: ['in-review', 'initiated'],
          created_at: {
            before: moment()
              .subtract(review_time, 'hours')
              .format(SEARCH_DATE_FORMAT),
            after: moment()
              .subtract(review_time, 'hours')
              .subtract(TWO_WEEKS_IN_DAYS, 'days')
              .format(SEARCH_DATE_FORMAT)
          }
        }
      }
    ],
    tooltip: ({ review_time }) =>
      `Notify: when a PR has been merged and was open for more than ${formatReviewTimeDaysAndHours(
        review_time
      )}`,
    headsUpTooltip: ({ review_time }) =>
      `Heads-up: when a PR has been opened for ${formatReviewTimeDaysAndHours(
        review_time
      )}`,
    ...defaultEnabledValues,
    similarNotifications: [
      SettingValues.REVIEW_TOO_LONG_NOTIFICATION,
      SettingValues.REVIEW_REQUEST_HANGING_NOTIFICATION
    ]
  }
});

export const workAtRiskNotification = () => ({
  [SettingValues.WORK_AT_RISK_NOTIFICATION]: {
    widgetId: 32,
    name: METRICS_METADATA[32].settingTitle,
    description: ({ pr_size, rework_refactor }) => (
      <>
        Identify pull requests that have more than <b>{pr_size} code changes</b>{' '}
        and a refactor or rework rate of <b>{rework_refactor}%</b> or more
      </>
    ),
    thresholds: ({ pr_size, rework_refactor }) => [
      {
        pr_size: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                A pull request has more than <b>{pr_size}</b> code changes
              </Text>
            </Flex>
          ),
          sliderSteps: 10
        },
        rework_refactor: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                Rework / Refactor size is above: <b>{rework_refactor}</b>
                <b>%</b>
              </Text>
            </Flex>
          )
        }
      }
    ],
    isHeadsUp: true,
    prediction: ({ pr_size, rework_refactor, startDate, endDate }) => [
      {
        type: notificationPredictionType.BRANCHES,
        filters: {
          new_work_percentage: { between: [0, 100 - rework_refactor] },
          pr_size: { gt: pr_size },
          updated_at: { before: endDate, after: startDate }
        }
      }
    ],
    tooltip: () => 'Notify: when a PR containing work at risk has been merged',
    headsUpTooltip: () =>
      'Heads-up: when a PR containing work at risk has been opened',
    ...defaultEnabledValues
  }
});

export const mergedWithoutReviewNotification = () => ({
  [SettingValues.MERGED_WITHOUT_REVIEW_NOTIFICATION]: {
    widgetId: 31,
    name: METRICS_METADATA[31].settingTitle,
    description: ({ pr_size }) => (
      <>
        Do not merge pull requests with more than <b>{pr_size} code changes</b>{' '}
        if it does not have a review
      </>
    ),
    thresholds: ({ pr_size }) => [
      {
        pr_size: {
          children: (
            <>
              <Flex {...flexStyle}>
                <Text {...textStyle}>
                  A pull request was merged without review,
                </Text>
              </Flex>
              <Flex {...flexStyle} pt='small'>
                <Text {...textStyle}>
                  with more than: <b>{pr_size}</b> code changes
                </Text>
              </Flex>
            </>
          ),
          sliderSteps: 10
        }
      }
    ],
    prediction: ({ pr_size, startDate, endDate }) => [
      {
        type: notificationPredictionType.PRS,
        filters: {
          merged_at: { before: endDate, after: startDate },
          pr_size: { gt: pr_size },
          unreviewed: true
        }
      }
    ],
    tooltip: () => 'Notify: when a PR has been merged without reviews',
    ...violationEnabledKey
  }
});

export const mergedWithBasicReviewNotification = () => ({
  [SettingValues.MERGED_WITH_BASIC_REVIEW_NOTIFICATION]: {
    widgetId: 33,
    name: METRICS_METADATA[33].settingTitle,
    description: ({ pr_size, review_time }) => (
      <>
        Do not merge pull requests that have more than{' '}
        <b>{pr_size} code changes</b> and a review time of less than{' '}
        <b>{review_time} minutes</b>
      </>
    ),
    thresholds: ({ pr_size, review_time }) => [
      {
        pr_size: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                A pull request has more than: <b>{pr_size}</b> code changes
              </Text>
            </Flex>
          ),
          sliderSteps: 10
        },
        review_time: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                Review time is below <b>{review_time}</b> minutes
              </Text>
            </Flex>
          )
        }
      }
    ],
    prediction: ({ pr_size, review_time, startDate, endDate }) => [
      {
        type: notificationPredictionType.PRS,
        filters: {
          is_draft: false,
          pr_size: { gt: pr_size },
          review_time: { between: [0, review_time] },
          state: ['merged'],
          updated_at: { before: endDate, after: startDate }
        }
      }
    ],
    tooltip: () => 'Notify: when a PR has been merged with a basic review',
    ...violationEnabledKey
  }
});

export const reviewRequestHangingNotification = () => ({
  [SettingValues.REVIEW_REQUEST_HANGING_NOTIFICATION]: {
    widgetId: 30,
    name: METRICS_METADATA[30].settingTitle,
    description: ({ review_time }) => (
      <>
        Start pull request reviews within{' '}
        <b>{formatReviewTimeDaysAndHours(review_time)}</b> after being issued
      </>
    ),
    thresholds: ({ review_time }) => [
      {
        review_time: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                A pull request is waiting for review for more than{' '}
                <b>{review_time}</b>{' '}
                {pluralize(review_time, 'hour', 's', false)}{' '}
                {addVerboseReviewTimeIfNeeded(review_time)}
              </Text>
            </Flex>
          ),
          minMaxFormatter: val => val
        }
      }
    ],
    isHeadsUp: true,
    prediction: ({ review_time }) => [
      {
        type: notificationPredictionType.PRS,
        filters: {
          is_draft: false,
          state: ['initiated'],
          updated_at: {
            before: moment()
              .subtract(review_time, 'hours')
              .format(SEARCH_DATE_FORMAT),
            after: moment()
              .subtract(review_time, 'hours')
              .subtract(TWO_WEEKS_IN_DAYS, 'days')
              .format(SEARCH_DATE_FORMAT)
          }
        }
      }
    ],
    tooltip: ({ review_time }) =>
      `Notify: when a PR with a long pick-up time has been merged`,
    headsUpTooltip: ({ review_time }) =>
      `Heads-up: when a PR is waiting to be reviewed for ${formatReviewTimeDaysAndHours(
        review_time
      )}`,
    ...defaultEnabledValues,
    similarNotifications: [SettingValues.PULL_REQUESTS_OPEN_MORE_THAN]
  }
});

export const reviewTooLongNotification = () => ({
  [SettingValues.REVIEW_TOO_LONG_NOTIFICATION]: {
    widgetId: 34,
    name: METRICS_METADATA[34].settingTitle,
    description: ({ review_time }) => (
      <>
        Complete pull request reviews in less than{' '}
        <b>{formatReviewTimeDaysAndHours(review_time)}</b>
      </>
    ),
    thresholds: ({ review_time }) => [
      {
        review_time: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                A review takes more than <b>{review_time}</b>{' '}
                {pluralize(review_time, 'hour', 's', false)}{' '}
                {addVerboseReviewTimeIfNeeded(review_time)}
              </Text>
            </Flex>
          ),
          minMaxFormatter: val => val
        }
      }
    ],
    isHeadsUp: true,
    prediction: ({ review_time }) => [
      {
        type: notificationPredictionType.PRS,
        filters: {
          is_draft: false,
          state: ['in-review'],
          created_at: {
            before: moment()
              .subtract(review_time, 'hours')
              .format(SEARCH_DATE_FORMAT),
            after: moment()
              .subtract(review_time, 'hours')
              .subtract(TWO_WEEKS_IN_DAYS, 'days')
              .format(SEARCH_DATE_FORMAT)
          }
        }
      }
    ],
    tooltip: () => `Notify: when a PR has been merged after a long review`,
    headsUpTooltip: ({ review_time }) =>
      `Heads-up: when a PR is in review for ${formatReviewTimeDaysAndHours(
        review_time
      )}`,
    ...defaultEnabledValues,
    similarNotifications: [SettingValues.PULL_REQUESTS_OPEN_MORE_THAN]
  }
});

export const dailyDigestNotificationThresholds = () => ({
  [SettingValues.DAILY_DIGEST_NOTIFICATION_THRESHOLDS]: {
    name: 'Daily Digest',
    description: ({ code_changes, comments_count }) => (
      <>
        Receive a daily digest of open pull requests that have more than{' '}
        <b>{code_changes} code changes</b>, stuck work and pull requests with
        more than <b>{comments_count} comments</b>
      </>
    ),
    withEstimation: false,
    getInModalDescription: args => {
      const dailyTime = getFormattedDailyTime(args);
      const displayDailyTime = formatUTCToTimezone(
        dailyTime,
        TIME_PICKER_FORMAT
      );

      const { history, toggleModal } = args;
      return (
        <Flex direction='column' pb='medium'>
          <Text {...textStyle} pb='0.5rem' lineHeight={LINE_HEIGHT.medium}>
            Slack daily digest will arrive every workday at {displayDailyTime}{' '}
            {getTimezone()} - just in time to prepare for your daily meeting
          </Text>
          <LBButton
            variant='ghost'
            onClick={() => {
              toggleModal();
              history.push(AppRouter.GENERAL.path);
            }}
          >
            update daily meeting hour
          </LBButton>
        </Flex>
      );
    },
    thresholds: ({ code_changes, comments_count }) => [
      {
        code_changes: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                Issues have more than <b>{code_changes}</b> code changes
              </Text>
            </Flex>
          )
        },
        comments_count: {
          children: (
            <Flex {...flexStyle}>
              <Text {...textStyle}>
                Pull requests have more than <b>{comments_count}</b> comments
              </Text>
            </Flex>
          )
        }
      }
    ],
    prediction: () => [{}],
    tooltip: () => `Turn on daily digest`,
    ...enabledKey
  }
});
